<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import SettingsSection from 'dashboard/components/SettingsSection.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import { useAlert } from 'dashboard/composables';
import {
  useStore,
  useMapGetter,
  useFunctionGetter,
} from 'dashboard/composables/store';
import { defineProps } from 'vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  assistantId: {
    type: Number,
    default: null,
  },
});

const store = useStore();
const accountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const webSearchAvailable = computed(
  () =>
    !isFeatureEnabledonAccount.value(accountId.value, 'eu_data_residency')
);

const name = ref('');
const task = ref('');
const humanEscalation = ref('');
const humanEscalationConfigItem = ref('');
const humanEscalationConfigValue = ref('');
const replyMode = ref('reply');
const reasoningBudget = ref('minimal');
const webSearch = ref(false);
const webSearchDomains = ref('');
const errors = ref([]);

const humanEscalationOptions = useMapGetter(
  'assistantWizard/getHumanEscalationOptions'
);
const tasks = useMapGetter('assistantWizard/getTasks');
const activeTraits = useMapGetter('assistantWizard/getActiveTraits');
const teamsList = useMapGetter('teams/getTeams');

const uiFlags = useMapGetter('assistants/getUIFlags');
const hasAvailableTeams = computed(() =>
  teamsList.value.some(team => team.allow_auto_assign)
);

const activeAssistant = useFunctionGetter(
  'assistants/getAssistant',
  props.assistantId
);

const filteredTeams = computed(() => {
  return teamsList.value?.filter(team => team && team.allow_auto_assign) || [];
});

const selectedTeamId = computed(() => {
  if (
    filteredTeams.value.some(
      team => team.id === humanEscalationConfigValue.value
    )
  ) {
    return humanEscalationConfigValue.value;
  }
  return filteredTeams.value[0]?.id || '';
});

const ensureDefaultTeamSelection = () => {
  if (humanEscalation.value !== 'assign_team' || !hasAvailableTeams.value) {
    return;
  }

  const selectedOption = humanEscalationOptions.value.find(
    ({ key }) => key === humanEscalation.value
  );
  if (selectedOption && !humanEscalationConfigItem.value) {
    humanEscalationConfigItem.value = selectedOption.configurationKey;
  }

  if (selectedTeamId.value !== humanEscalationConfigValue.value) {
    humanEscalationConfigValue.value = selectedTeamId.value;
  }
};

const refreshTeams = async () => {
  if (document.visibilityState === 'hidden') {
    return;
  }
  await store.dispatch('teams/get');
  ensureDefaultTeamSelection();
};

const updateAssistant = async () => {
  try {
    await store.dispatch('assistants/update', {
      id: activeAssistant.value.id,
      agent_id: activeAssistant.value.agent_id,
      inbox_id: activeAssistant.value.inbox_id,
      name: name.value,
      task: task.value,
      traits: activeTraits.value,
      config: {
        reply_mode: replyMode.value,
        reasoning_budget: reasoningBudget.value,
        web_search: webSearch.value,
        web_search_domains: webSearchDomains.value
          .split(/[\s,]+/)
          .map(domain =>
            domain
              .trim()
              .toLowerCase()
              .replace(/^https?:\/\//, '')
              .split(/[/?#]/)[0]
              .replace(/\.$/, '')
          )
          .filter(
            (domain, index, domains) =>
              domain.includes('.') && domains.indexOf(domain) === index
          )
          .slice(0, 100),
      },
      human_escalation: {
        type: humanEscalation.value,
        config: {
          item: humanEscalationConfigItem.value,
          value: humanEscalationConfigValue.value,
        },
      },
    });
    errors.value = [];
  } catch (e) {
    errors.value = [];
    errors.value.push('Make sure all fields are filled in correctly.');
    useAlert('Could not update settings, try again!');
  }
};

onMounted(async () => {
  await refreshTeams();
  if (activeAssistant.value) {
    name.value = activeAssistant.value.name || '';
    task.value = activeAssistant.value.task || '';
    humanEscalation.value = activeAssistant.value.human_escalation?.type || '';
    humanEscalationConfigItem.value =
      activeAssistant.value.human_escalation?.config?.item || '';
    humanEscalationConfigValue.value =
      activeAssistant.value.human_escalation?.config?.value || '';
    replyMode.value = activeAssistant.value.reply_mode || 'reply';
    reasoningBudget.value = activeAssistant.value.reasoning_budget || 'minimal';
    webSearch.value = Boolean(activeAssistant.value.web_search);
    webSearchDomains.value = (activeAssistant.value.web_search_domains || []).join('\n');
  }
  ensureDefaultTeamSelection();
  window.addEventListener('focus', refreshTeams);
  document.addEventListener('visibilitychange', refreshTeams);
});

const onHumanEscalationChange = e => {
  humanEscalationConfigItem.value = '';
  humanEscalationConfigValue.value = '';
  if (humanEscalation.value === e.currentTarget.id) {
    humanEscalation.value = '';
    return;
  }
  humanEscalation.value = e.currentTarget.id;
  humanEscalationConfigItem.value = humanEscalationOptions.value.find(
    ({ key }) => key === humanEscalation.value
  ).configurationKey;
  ensureDefaultTeamSelection();
};

watch([humanEscalation, filteredTeams], () => {
  ensureDefaultTeamSelection();
});

onBeforeUnmount(() => {
  window.removeEventListener('focus', refreshTeams);
  document.removeEventListener('visibilitychange', refreshTeams);
});
</script>

<template>
  <div class="mx-8">
    <LoadingState v-if="uiFlags.isFetching || uiFlags.isFetchingAgentBot" />
    <form
      v-else
      class="mx-0 flex flex-wrap"
      @submit.prevent="updateActiveAgentBot"
    >
      <SettingsSection
        :title="$t('ASSISTANT_MGMT.CONFIGURATION.TITLE')"
        :sub-title="$t('ASSISTANT_MGMT.CONFIGURATION.DESC')"
      >
        <form class="flex flex-col" @submit.prevent="updateAssistant">
          <input
            v-model.trim="name"
            type="text"
            class="pb-4 dark:!bg-neutral-950"
            :label="$t('ASSISTANT_MGMT.CONFIGURATION.FORM.NAME.LABEL')"
          />

          <label class="input-container pb-4">
            <span>{{
              $t('ASSISTANT_MGMT.CONFIGURATION.FORM.TASK.LABEL')
            }}</span>
            <select
              id="task"
              v-model="task"
              class="bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 border-slate-200 dark:border-slate-600"
            >
              <option
                v-for="l_task in tasks"
                :key="l_task.key"
                :value="l_task.key"
              >
                {{ l_task.name }}
              </option>
            </select>
          </label>

          <label class="input-container pb-4">
            <span>{{
              $t('ASSISTANT_MGMT.CONFIGURATION.FORM.REPLY_MODE.LABEL')
            }}</span>
            <select
              id="reply_mode"
              v-model="replyMode"
              class="bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 border-slate-200 dark:border-slate-600"
            >
              <option value="reply">
                {{
                  $t(
                    'ASSISTANT_MGMT.CONFIGURATION.FORM.REPLY_MODE.OPTIONS.REPLY'
                  )
                }}
              </option>
              <option value="note">
                {{
                  $t(
                    'ASSISTANT_MGMT.CONFIGURATION.FORM.REPLY_MODE.OPTIONS.NOTE'
                  )
                }}
              </option>
            </select>
          </label>

          <label class="input-container pb-4">
            <span>{{
              $t('ASSISTANT_MGMT.CONFIGURATION.FORM.REASONING_BUDGET.LABEL')
            }}</span>
            <select
              id="reasoning_budget"
              v-model="reasoningBudget"
              class="bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 border-slate-200 dark:border-slate-600"
            >
              <option value="off">
                {{
                  $t(
                    'ASSISTANT_MGMT.CONFIGURATION.FORM.REASONING_BUDGET.OPTIONS.OFF'
                  )
                }}
              </option>
              <option value="minimal">
                {{
                  $t(
                    'ASSISTANT_MGMT.CONFIGURATION.FORM.REASONING_BUDGET.OPTIONS.MINIMAL'
                  )
                }}
              </option>
              <option value="low">
                {{
                  $t(
                    'ASSISTANT_MGMT.CONFIGURATION.FORM.REASONING_BUDGET.OPTIONS.LOW'
                  )
                }}
              </option>
              <option value="medium">
                {{
                  $t(
                    'ASSISTANT_MGMT.CONFIGURATION.FORM.REASONING_BUDGET.OPTIONS.MEDIUM'
                  )
                }}
              </option>
              <option value="high">
                {{
                  $t(
                    'ASSISTANT_MGMT.CONFIGURATION.FORM.REASONING_BUDGET.OPTIONS.HIGH'
                  )
                }}
              </option>
            </select>
            <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
              {{
                $t('ASSISTANT_MGMT.CONFIGURATION.FORM.REASONING_BUDGET.HELP')
              }}
            </p>
          </label>

          <label
            v-if="webSearchAvailable"
            class="flex items-start gap-2 pb-4"
            for="web_search"
          >
            <input
              id="web_search"
              v-model="webSearch"
              type="checkbox"
              class="mt-1"
            />
            <span>
              <span class="block">{{
                $t('ASSISTANT_MGMT.CONFIGURATION.FORM.WEB_SEARCH.LABEL')
              }}</span>
              <span class="mt-1 block text-xs text-slate-500 dark:text-slate-400">{{
                $t('ASSISTANT_MGMT.CONFIGURATION.FORM.WEB_SEARCH.HELP')
              }}</span>
            </span>
          </label>

          <label v-if="webSearchAvailable && webSearch" class="input-container pb-4">
            <span>{{
              $t('ASSISTANT_MGMT.CONFIGURATION.FORM.WEB_SEARCH.DOMAINS_LABEL')
            }}</span>
            <textarea
              v-model="webSearchDomains"
              rows="4"
              class="bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 border-slate-200 dark:border-slate-600"
            />
            <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
              {{ $t('ASSISTANT_MGMT.CONFIGURATION.FORM.WEB_SEARCH.DOMAINS_HELP') }}
            </p>
          </label>

          <label class="input-container sm:w-full">
            <span class="flex leading-tight mb-4">
              {{
                $t('ASSISTANT_MGMT.WIZARD.HUMANESCALATION_LABEL', {
                  name: name.value,
                })
              }}
            </span>
            <div class="flex flex-col md:flex-row gap-4 w-full pb-4">
              <div
                v-for="option in humanEscalationOptions"
                :id="option.key"
                :key="option.key"
                class="box flex flex-col items-center w-full p-6 border border-2 rounded-xl text-center cursor-pointer bg-white dark:bg-neutral-900 border-neutral-300 dark:border-neutral-700 hover:border-woot-600 bg-white"
                :class="{ active: humanEscalation == option.key }"
                @click="onHumanEscalationChange"
              >
                <div
                  class="check-container bg-neutral-100 dark:bg-neutral-800 rounded-full text-white mb-2 p-2"
                >
                  <fluent-icon v-if="option.icon" :icon="option.icon" />
                </div>
                <h3 class="mb-2 font-semibold text-lg">{{ option.title }}</h3>
                <p class="leading-tight w-full">{{ option.description }}</p>
              </div>
            </div>
            <div
              v-if="humanEscalation === 'assign_team'"
              class="flex flex-col gap-4 items-center py-4 w-full"
            >
              <template v-if="hasAvailableTeams">
                <span class="flex leading-tight"
                  >{{ name }} will pass the conversation to the Rookoo help desk
                  for one of your teammates to resolve.
                </span>
                <select
                  id="team"
                  v-model="humanEscalationConfigValue"
                  class="rounded-md border-2 border-slate-100 text-center w-80 h-10 leading-5 w-full"
                >
                  <option
                    v-for="team in filteredTeams"
                    :key="team.id"
                    :value="team.id"
                  >
                    {{ team.name }}
                  </option>
                </select>
              </template>
              <template v-else>
                <p class="mb-4">
                  You need to create a team first to enable human escalation.
                </p>
                <router-link
                  :to="{ name: 'settings_teams_new' }"
                  target="_blank"
                >
                  <NextButton
                    type="button"
                    icon="i-lucide-circle-plus"
                    :label="$t('Create team')"
                  />
                </router-link>
              </template>
            </div>

            <div
              v-if="humanEscalation === 'close_conversation'"
              class="flex flex-col gap-4 items-center py-4 w-full"
            >
              <span class="flex leading-tight"
                >Tell {{ name }} what to do before closing the
                conversation.</span
              >
              <textarea
                id="instructions"
                v-model="humanEscalationConfigValue"
                name="instructions"
              />
            </div>
          </label>

          <label v-if="errors.length" class="error">
            <span v-for="(error, index) in errors" :key="index" class="message">
              {{ error }}
            </span>
          </label>

          <div class="button-container mt-4">
            <NextButton
              :label="$t('ASSISTANT_MGMT.CONFIGURATION.SUBMIT')"
              :is-loading="uiFlags.isUpdating"
            />
          </div>
        </form>
      </SettingsSection>
    </form>
  </div>
</template>

<style scoped lang="scss">
.box.active {
  border-color: #645298;
  background: #f0eef6;
}
.dark .box.active {
  border-color: #645298;
  background: #292932;
}
.box.active p {
  font-weight: 600;
}
.box.active .check-container {
  background: #645298;
}
</style>
